import React, { useMemo, useState, useEffect } from 'react';
import { useIntl, useParams, history, getLocale } from 'umi';
import { Layout, Tab } from 'tea-component';
import Tabs from '@intelligence/components/NewIconTabs';
import ActiveRevDown from './ActiveRevDown';
import StoreRank from './StoreRank';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { getValidActiveId } from '@/pages/intelligence/utils/commonUtils';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';
import PermissionGuard from '@intelligence/v3/pages/TopCharts/MobileGames/_components/PermissionGuard';

import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { pointInter as IBuriedPointCfg } from '@/pages/home/utils/buriedPoint';
import type { IMobileComparePageId } from '@/pages/intelligence/types/mobileCompare';

// mobile compare的所有样式 都是这里引入的，其他地方没有引入样式的入口了
import '@/pages/intelligence/assets/scss/Compare/mobileGames.scss';

const { Body, Content } = Layout;

const ENTITY_TYPE = 'mobile';

const AU_REVENUE_DOWNLOADS: IMobileComparePageId[] = ['dau', 'revenue', 'download'];

const PERMISSION_GUARD_STYLE: React.CSSProperties = {
  height: 'calc(100vh - 60px - 54px - 40px)',
};

const PAGE_LOG_CFGS: {
  [key in IMobileComparePageId]: IBuriedPointCfg;
} = {
  dau: dbiPageLogMes.compareMobileActiveUsers,
  revenue: dbiPageLogMes.compareMobileRevenue,
  download: dbiPageLogMes.compareMobileDownloads,
  storeRank: dbiPageLogMes.compareMobileStoreRank,
};

export const getParamFromUrl = (str = '') => {
  const [mainMenu, tab, childTab] = str?.trim()?.split('/') || [];
  return {
    mainMenu: mainMenu || '',
    tab: tab || '',
    childTab: childTab || '',
  };
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IMobileCompareProps extends IBaseProps {}

const MobileCompare: React.FC<IMobileCompareProps> = (props) => {
  const {} = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const { tab: pathActiveId } = useParams<any>();

  const tabs: {
    id: IMobileComparePageId;
    label: string;
  }[] = [
    {
      id: 'dau',
      label: formatMessage({ id: 'dbi_active_users' }),
    },
    {
      id: 'revenue',
      label: formatMessage({ id: 'dbi_revenue' }),
    },
    {
      id: 'download',
      label: formatMessage({ id: 'dbi_downloads' }),
    },
    {
      id: 'storeRank',
      label: formatMessage({ id: 'dbi_storeRank' }),
    },
  ];

  const initialActiveId = getValidActiveId(pathActiveId, {
    tabs,
    defaultActiveId: tabs[0].id,
  }) as IMobileComparePageId;

  const [activeId, setActiveId] = useState<IMobileComparePageId>(initialActiveId);
  const activeName = useMemo(() => {
    const tab = tabs?.find((item) => item?.id === activeId);
    return tab?.label || activeId || '';
  }, [locale, activeId]);

  const handleActive = (tab: Tab) => {
    const activeIdTmp = tab?.id as IMobileComparePageId;
    if (!activeIdTmp) return;
    setActiveId(activeIdTmp);
    history.replace(`/intelligence/compare/mobileGames/${activeIdTmp}`);
  };

  useEffect(() => {
    const pageLogCfg = PAGE_LOG_CFGS[activeId];
    if (pageLogCfg) {
      buriedPointPage(pageLogCfg);
    }
  }, [activeId]);

  return (
    <Layout className="dbi-compare-mobile">
      <Body>
        <Content>
          <Content.Header
            className="contentHead-normal"
            title={
              <div className="dbi-compare-mobile_title">
                <span>{formatMessage({ id: 'dbi_mobile_games' })}</span>
              </div>
            }
          >
            <div className="header-tab dbi-header-tab">
              <Tabs
                className="dbi-page-tabs"
                newMarkIdsMappingKey="CompareMobileGames"
                ceiling={true}
                tabs={tabs}
                animated={false}
                activeId={activeId}
                onActive={handleActive}
              />
            </div>
          </Content.Header>

          <Content.Body full>
            <PermissionGuard pageId={PAGE_LOG_CFGS[activeId].pageId} style={PERMISSION_GUARD_STYLE}>
              <MarqueeBanner />
              {AU_REVENUE_DOWNLOADS.includes(activeId) && (
                <ActiveRevDown
                  key={activeId}
                  entityType={ENTITY_TYPE}
                  mainSegment={activeId}
                  mainSegmentText={activeName}
                />
              )}
              {activeId === 'storeRank' && (
                <StoreRank
                  key={activeId}
                  entityType={ENTITY_TYPE}
                  mainSegment={activeId}
                  mainSegmentText={activeName}
                />
              )}
            </PermissionGuard>
          </Content.Body>
        </Content>
      </Body>
    </Layout>
  );
};

export default MobileCompare;
